import { useEffect, useState } from 'react';
import { useAuth } from '~/context/AuthProvider';
import CardDataStats from './Cards/CardDataStats';
import ReferralsTab from '~/components/MedicalProfessionalDetail/ReferralsTab';
import NeedVerificationTab from '~/components/MedicalProfessionalDetail/NeedVerificationTab';
import VerifiedPatientsTab from '~/components/MedicalProfessionalDetail/VerifiedPatientsTab';
import Panel from '~/components/Panel';
import Tabs from '~/components/Tabs';
import { fetchMedicalProfessionals } from '~/services/medical-professionals';

interface Props {
    onMedProfMatched?: (medProf: any) => void;
}

// Shared "the logged-in med pro's clients" panel: stats cards + Need
// Verification / Verified Patients / Referrals tabs. Used by:
//   - MedicalProfessionalDashboard (with a profile sidebar wrapped around it)
//   - The Clients page (rendered standalone)
const MedProfClientsList = ({ onMedProfMatched }: Props) => {
    const { person } = useAuth();
    const [openTab, setOpenTab] = useState(1);
    const [medProfId, setMedProfId] = useState<number | null>(null);
    const [verifiedCount, setVerifiedCount] = useState<number>(0);
    const [unverifiedCount, setUnverifiedCount] = useState<number>(0);
    const [lookupFailed, setLookupFailed] = useState(false);

    useEffect(() => {
        if (!person?.email) return;
        let cancelled = false;
        (async () => {
            try {
                const resp = await fetchMedicalProfessionals({
                    name: person.email,
                });
                if (cancelled) return;
                const match = resp?.results?.[0];
                if (!match?.id) {
                    setLookupFailed(true);
                    return;
                }
                setMedProfId(match.id);
                setVerifiedCount(match.verified_patient_count ?? 0);
                setUnverifiedCount(match.unverified_patient_count ?? 0);
                onMedProfMatched?.(match);
            } catch (e) {
                if (!cancelled) setLookupFailed(true);
                console.error('Med pro lookup failed:', e);
            }
        })();
        return () => {
            cancelled = true;
        };
        // onMedProfMatched intentionally not in deps — callers pass a stable
        // reference; including it would re-run the lookup on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [person?.email]);

    const tabNames = [
        { name: 'Need Verification', count: unverifiedCount },
        { name: 'Verified Patients', count: verifiedCount },
        { name: 'Referrals', count: 0 },
    ];

    return (
        <>
            <section className='grid grid-cols-1 gap-x-7.5 md:grid-cols-3'>
                <CardDataStats
                    title='Need Verification'
                    total={unverifiedCount}
                    color='text-[#952d46]'
                    borderColor='border-[#f5eaec]'
                    bgColor='bg-[#f5eaec]'
                />
                <CardDataStats
                    title='Verified Patients'
                    total={verifiedCount}
                    color='text-[#00aeb3]'
                    borderColor='border-[#e5f7f7]'
                    bgColor='bg-[#e5f7f7]'
                />
                <CardDataStats
                    title='Referrals'
                    total={0}
                    color='text-[#f47d29]'
                    borderColor='border-[#fef2ea]'
                    bgColor='bg-[#fef2ea]'
                />
            </section>

            <Panel>
                {lookupFailed ? (
                    <div className='p-4 text-[14px] text-[#949599]'>
                        Could not find a medical professional record for this
                        account. Please contact support.
                    </div>
                ) : medProfId ? (
                    <Tabs
                        tabNames={tabNames}
                        openTab={openTab}
                        setOpenTab={setOpenTab}
                    >
                        {openTab == 1 ? (
                            <NeedVerificationTab
                                id={medProfId}
                                clientLinkMode='verify'
                            />
                        ) : (
                            ''
                        )}
                        {openTab == 2 ? (
                            <VerifiedPatientsTab
                                id={medProfId}
                                clientLinkMode='verify'
                            />
                        ) : (
                            ''
                        )}
                        {openTab == 3 ? <ReferralsTab /> : ''}
                    </Tabs>
                ) : (
                    <div className='p-4 text-[14px] text-[#949599]'>
                        Loading…
                    </div>
                )}
            </Panel>
        </>
    );
};

export default MedProfClientsList;
